import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import TaskLocationSelectInput from '@atom/components/common/workOrderDetail/locationsAndAssetsSection/taskLocationModal/TaskLocationSelectInput';
import {
  getInitialOptions,
  mapAutocompleteToOptionsState,
  mapDataToSimpleInput,
} from '@atom/components/common/workOrderDetail/locationsAndAssetsSection/taskLocationUtilities';
import WorkOrdersContext, {
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { TASK_LOCATION_DATA_AUTOCOMPLETE } from '@atom/graph/taskLocation';
import { TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  LocationDataState,
  LocationDataTitle,
  LocationOptionsState,
  TaskLocationAutocomplete,
  TaskLocationSimpleInput,
} from '@atom/types/taskLocation';
import { LocationDataFilterInput } from '@atom/types/work';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './workOrdersFilters.module.css';

const UDOT_SELECT_FIELDS = [LocationDataTitle.STATION, LocationDataTitle.ROUTE];

const UDOT_FILTER_DEFAULTS: LocationDataState = {
  [LocationDataTitle.STATION]: null,
  [LocationDataTitle.ROUTE]: null,
};

const styles = {
  separator: {
    margin: '0 1rem',
    paddingBottom: '0.5rem',
  },
};

const WorkFilterLocationUdotFilters = () => {
  const { workOrdersInputCart, filtersDisabled, dispatch } =
    useContext(WorkOrdersContext);

  const locationFilter = workOrdersInputCart?.location;

  const [options, setOptions] =
    useState<LocationOptionsState>(getInitialOptions());

  const [autocompleteData, { loading }] = useMutation<
    { taskLocationDataAutocomplete: TaskLocationAutocomplete },
    { input: TaskLocationSimpleInput }
  >(TASK_LOCATION_DATA_AUTOCOMPLETE);

  const fieldValues = R.pathOr(
    [],
    ['fields'],
    locationFilter,
  ).reduce<LocationDataState>(
    (acc, field) => ({ ...acc, [field.title]: field.value }),
    {},
  );

  const values: LocationDataState = {
    ...UDOT_FILTER_DEFAULTS,
    ...fieldValues,
    [LocationDataTitle.START_MILEPOST]: locationFilter?.startMilepost ?? '',
    [LocationDataTitle.END_MILEPOST]: locationFilter?.endMilepost ?? '',
  };

  useEffect(() => {
    const getAutocompleteData = async () => {
      const res = await autocompleteData({
        variables: {
          input: mapDataToSimpleInput(values, true),
        },
      });

      setOptions(
        mapAutocompleteToOptionsState(res?.data?.taskLocationDataAutocomplete),
      );
    };

    getAutocompleteData();
  }, [values[LocationDataTitle.STATION], values[LocationDataTitle.ROUTE]]);

  const updateValue = (property: LocationDataTitle, value: any) => {
    const updatedValues = { ...values, [property]: value };

    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property: 'location',
        value: {
          fields: UDOT_SELECT_FIELDS.filter(
            title => !isNilOrEmpty(updatedValues[title]),
          ).map(title => ({ title, value: updatedValues[title] })),
          startMilepost:
            updatedValues[LocationDataTitle.START_MILEPOST] !== ''
              ? updatedValues[LocationDataTitle.START_MILEPOST]
              : undefined,
          endMilepost:
            updatedValues[LocationDataTitle.END_MILEPOST] !== ''
              ? updatedValues[LocationDataTitle.END_MILEPOST]
              : undefined,
        } as LocationDataFilterInput,
      },
    });
  };

  const handleMilepostChange = (title: LocationDataTitle, value: any) => {
    if (value === '') {
      updateValue(title, '');
      return;
    }

    updateValue(title, Number(value));
  };

  const showRanges =
    !isNilOrEmpty(options[LocationDataTitle.START_MILEPOST]) &&
    !isNilOrEmpty(options[LocationDataTitle.END_MILEPOST]);

  const rangeSubtext = `Provide a range from ${options[LocationDataTitle.START_MILEPOST]} to ${
    options[LocationDataTitle.END_MILEPOST]
  } for accurate filtering.`;

  return (
    <>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.STATION}
          options={options[LocationDataTitle.STATION]}
          value={values[LocationDataTitle.STATION]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationStation_filter"
        />
      </div>
      <div className={classes.filterContainer}>
        <TaskLocationSelectInput
          property={LocationDataTitle.ROUTE}
          options={options[LocationDataTitle.ROUTE]}
          value={values[LocationDataTitle.ROUTE]}
          required={false}
          updateValue={updateValue}
          loading={loading}
          disabled={filtersDisabled}
          dataCy="locationRoute_filter"
        />
      </div>
      <div className={clsx(classes.filtersDateRange, classes.filterContainer)}>
        <TextField
          type="number"
          value={values[LocationDataTitle.START_MILEPOST]}
          label="Start & End Milepost"
          onChange={event =>
            handleMilepostChange(
              LocationDataTitle.START_MILEPOST,
              event.target.value,
            )
          }
          disabled={filtersDisabled}
          variant="standard"
          inputLabelColor={colors.neutral.dim}
          data-cy="locationStartMilepost_filter"
        />
        <span style={styles.separator}>to</span>
        <TextField
          type="number"
          value={values[LocationDataTitle.END_MILEPOST]}
          label=""
          onChange={event =>
            handleMilepostChange(
              LocationDataTitle.END_MILEPOST,
              event.target.value,
            )
          }
          disabled={filtersDisabled}
          variant="standard"
          data-cy="locationEndMilepost_filter"
        />
      </div>
      {showRanges && <div className={classes.rangeSubtext}>{rangeSubtext}</div>}
    </>
  );
};

export default WorkFilterLocationUdotFilters;
